import { Injectable } from '@angular/core';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { FirebaseService } from '../firebase/firebase.service';
import { UserService } from '../user/user.service';
import { LoggingChannels, LoggingService } from '../logging/logging.service';

export type ThemeMode = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public mode: ThemeMode = 'light';

  private systemDarkQuery = window.matchMedia('(prefers-color-scheme: dark)');

  constructor(
    private localStorage: LocalStorageService,
    private firebase: FirebaseService,
    private user: UserService,
    private logging: LoggingService,
  ) {
    this.initialize();
  }

  public initialize(): void {
    const stored = this.localStorage.getItem('theme') as ThemeMode;
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      this.mode = stored;
    }
    this.applyTheme();

    this.systemDarkQuery.addEventListener('change', () => {
      if (this.mode === 'system') {
        this.applyTheme();
      }
    });
  }

  public setMode(mode: ThemeMode): void {
    this.mode = mode;
    this.localStorage.setItem('theme', mode);
    this.applyTheme();
    this.syncToFirestore();
  }

  public async loadUserTheme(): Promise<void> {
    try {
      const uid = this.user.user?.uid;
      if (!uid) {
        return;
      }
      const doc = await this.firebase.firestore.docGet(`users/${uid}`);
      if (doc.exists()) {
        const data: any = doc.data();
        if (data.theme && ['light', 'dark', 'system'].includes(data.theme)) {
          const localTheme = this.localStorage.getItem('theme');
          if (!localTheme) {
            this.mode = data.theme;
            this.localStorage.setItem('theme', data.theme);
            this.applyTheme();
          }
        }
      }
    } catch (err) {
      this.logging.log('ThemeService: error loading user theme', LoggingChannels.Default, err);
    }
  }

  private applyTheme(): void {
    const isDark = this.mode === 'dark' || (this.mode === 'system' && this.systemDarkQuery.matches);
    document.body.classList.toggle('dark', isDark);
  }

  private async syncToFirestore(): Promise<void> {
    try {
      const uid = this.user.user?.uid;
      if (!uid) {
        return;
      }
      await this.firebase.firestore.docSet(`users/${uid}`, {
        theme: this.mode,
      }, { merge: true });
    } catch (err) {
      this.logging.log('ThemeService: error syncing theme to Firestore', LoggingChannels.Default, err);
    }
  }
}
